<template>
  <div>
    <p v-if="selectedIpo && selectedIpo.pre_sale" class="ipo-preorder-notice">
      {{ $t('ipo.preorder') }}
    </p>

    <!-- Захиалгын төрөл (ЗГБ холимог) -->
    <div v-if="selectedIpo && String(selectedIpo.sell_type) === '3'" class="ipo-bond-pills">
      <button
        class="ipo-bond-pill"
        :class="{ 'ipo-bond-pill--active': orderPayload.order_type == 2 }"
        @click="$store.dispatch('ipoOrder/selectOrderType', 2)"
      >{{ $t('ipo.bond_type_2') }}</button>
      <button
        class="ipo-bond-pill"
        :class="{ 'ipo-bond-pill--active': orderPayload.order_type == 1 }"
        @click="$store.dispatch('ipoOrder/selectOrderType', 1)"
      >{{ $t('ipo.bond_type_1') }}</button>
    </div>

    <form @submit.prevent="submit" class="ipo-order-form">
      <!-- Тоо ширхэг -->
      <div class="ipo-form-group">
        <label class="ipo-form-label">{{ $t('ipo.quantity') }}</label>
        <input
          type="number"
          min="1"
          class="ipo-form-input"
          :class="{ 'ipo-form-input--error': $v.size.$error }"
          v-model.number="size"
          @input="$v.size.$touch()"
        />
        <span v-if="$v.size.$error" class="ipo-form-error">{{ $t('ipo.correct_value') }}</span>
      </div>

      <!-- Хүү (ЗГБ өрсөлдөөнт / sell_type 4) -->
      <div
        v-if="selectedIpo && (orderPayload.order_type == 1 && String(selectedIpo.sell_type) === '3') || String(selectedIpo && selectedIpo.sell_type) === '4'"
        class="ipo-form-group"
      >
        <label class="ipo-form-label">{{ $t('ipo.interest') }}</label>
        <input
          type="number"
          min="0.001"
          max="100"
          step="0.1"
          class="ipo-form-input"
          :class="{ 'ipo-form-input--error': $v.interest.$error }"
          v-model.number="interest"
          @input="$v.interest.$touch()"
        />
        <span v-if="$v.interest.$error" class="ipo-form-error">{{ $t('ipo.correct_value') }}</span>
      </div>

      <!-- Шимтгэлийн тооцоолол -->
      <ipo-fee-calculator v-if="feeValue && totalPrice > 0" :fee="feeValue" :total="totalPrice" />

      <!-- Идэвхтэй захиалгын төлөв -->
      <div v-if="activeOrder" class="ipo-status-row">
        <span>{{ $t('ipo.status') }}:</span>
        <span>{{ orderStateName(activeOrder.state) }}</span>
      </div>

      <!-- % товчнууд -->
      <div v-if="!hasActiveOrder" class="ipo-pct-btns">
        <button type="button" class="ipo-pct-btn" @click="calcFromPct(25)">25%</button>
        <button type="button" class="ipo-pct-btn" @click="calcFromPct(50)">50%</button>
        <button type="button" class="ipo-pct-btn" @click="calcFromPct(75)">75%</button>
        <button type="button" class="ipo-pct-btn" @click="calcFromPct(100)">100%</button>
      </div>

      <!-- Үйлдлийн товчнууд -->
      <div class="ipo-action-row">
        <button
          v-if="hasActiveOrder"
          type="button"
          class="ipo-btn-danger"
          :disabled="cancelling"
          @click="handleCancel"
        >
          <b-spinner v-if="cancelling" small />
          <i v-else class="fas fa-trash"></i>
          {{ $t('ipo.cancel') }}
        </button>
        <button
          v-if="!hasActiveOrder"
          type="submit"
          class="ipo-btn-primary"
          :disabled="submitting"
        >
          <b-spinner v-if="submitting" small />
          <i v-else class="fas fa-check-circle"></i>
          {{ $t('ipo.make_order') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { required, integer, minValue, maxValue } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'
import IpoFeeCalculator from './IpoFeeCalculator.vue'

const ORDER_STATES = {
  1000: 'Хүлээгдэж байна',
  2000: 'Дансны үлдэгдэл хүрэлцэхгүй',
  3000: 'Илгээгдсэн',
  4000: 'Биелсэн',
  5000: 'Цуцлагдсан',
}

export default {
  name: 'IpoMakeOrder',
  components: { IpoFeeCalculator },

  validations: {
    size: {
      required,
      integer,
      minValue: minValue(1),
      maxValue: maxValue(9999999999),
    },
    interest: {
      minValue: minValue(0.001),
      maxValue: maxValue(100),
    },
  },

  computed: {
    ...mapGetters('ipoOrder', [
      'selectedIpo', 'orderPayload', 'hasActiveOrder', 'activeOrder',
      'submitting', 'cancelling', 'includedFee',
    ]),
    size: {
      get() { return this.orderPayload.size },
      set(v) { this.$store.dispatch('ipoOrder/updateOrderPayload', { size: v }) },
    },
    interest: {
      get() { return this.orderPayload.interest },
      set(v) { this.$store.dispatch('ipoOrder/updateOrderPayload', { interest: v }) },
    },
    localBalance() {
      return this.$store.getters.localBalance
    },
    mseBalance() {
      return this.$store.getters.mseBalance || 0
    },
    totalPrice() {
      const { size } = this.orderPayload
      const price = parseFloat(this.selectedIpo?.detail?.unit_price ?? 0)
      return size > 0 && price > 0 ? parseFloat(size) * price : 0
    },
    feeValue() {
      const stype = this.selectedIpo?.type
      const fees = this.$store.getters['user/user']?.fees || []
      const match = fees.find((f) => f.stype == stype && f.side == 1)
      return match ? parseFloat(match.broker_fee) + parseFloat(match.transaction_fee) : 0
    },
    availableBalance() {
      const isNominal = this.selectedIpo?.gather_source === 'NOMINAL'
      return isNominal ? (this.localBalance?.availableBalance ?? 0) : this.mseBalance
    },
  },

  methods: {
    orderStateName(state) {
      return ORDER_STATES[state] || state
    },
    calcFromPct(pct) {
      const balance = this.availableBalance
      const unitPrice = this.selectedIpo?.detail?.unit_price ?? 1
      const possible = balance * pct / 100
      const feeMultiplier = 1 + (this.feeValue / 100)
      const qty = possible > 0 ? Math.floor(possible / (unitPrice * feeMultiplier)) : 0
      this.$store.dispatch('ipoOrder/updateOrderPayload', { size: qty })
      this.$v.size.$touch()
    },
    async submit() {
      this.$v.size.$touch()
      if (this.$v.size.$error) return

      const needsInterest =
        (this.orderPayload.order_type == 1 && String(this.selectedIpo?.sell_type) === '3') ||
        String(this.selectedIpo?.sell_type) === '4'

      if (needsInterest) {
        this.$v.interest.$touch()
        if (this.$v.interest.$error) return
      }

      const confirmed = await this.$swal({
        title: this.$t('ipo.confirm'),
        text: this.$t('ipo.confirm_order_text'),
        showCancelButton: true,
        confirmButtonText: this.$t('ipo.yes'),
        cancelButtonText: this.$t('ipo.no'),
      })
      if (!confirmed.isConfirmed) return

      await this.$store.dispatch('ipoOrder/sendOrder')
      this.$v.$reset()
    },
    async handleCancel() {
      const confirmed = await this.$swal({
        title: this.$t('ipo.confirm'),
        text: this.$t('ipo.confirm_cancel_text'),
        showCancelButton: true,
        confirmButtonText: this.$t('ipo.yes'),
        cancelButtonText: this.$t('ipo.no'),
      })
      if (!confirmed.isConfirmed) return
      await this.$store.dispatch('ipoOrder/cancelOrder')
    },
  },
}
</script>
